import {t} from "@lingui/core/macro";
import * as React from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {RequestInfo} from "@khan/shared-core";

import StoreBadge from "./store-badge.tsx";

type GooglePlayBadgeProps = {
    safeAppDownloadUrl: string;
    /**
     * Set to true if the badge will be displayed on a dark background.
     * Adjusts features like the color of the focus ring.
     */
    light?: boolean;
    style?: StyleType;
};

/**
 * Maps the locale codes used by Khan Academy to the locales used
 * by Google’s badges. This is effectively the intersection of locales
 * supported by Khan Academy and locales supported by Google.
 *
 * @see {@link https://play.google.com/intl/en_us/badges/|Badge Generator}
 *      for the full list of locales Google provides their badge in or
 *      to acquire the alt. text and locale codes for new locales.
 *
 * To find Google’s locale code use the badge generator to generate a
 * digital delivery, PNG image for your target locale. Then inspect the
 * HTML markup of the displayed badge image. The locale code appears in
 * the <img>’s `src` attribute. Translated alt. text appears in the
 * `alt` attribute.
 *
 * Alternatively, you can copy the badge-embed HTML then paste it
 * somewhere and inspect the `src` and `alt` attributes that way.
 */
const KA_LOCALE_TO_PLAY_LOCALE = {
    ar: "ar",
    az: "az",
    bg: "bg",
    bn: "bn",
    cs: "cs",
    da: "da",
    de: "de",
    el: "el",
    en: "en",
    es: "es",
    fa: "fa",
    fr: "fr",
    gu: "gu",
    he: "iw",
    hi: "hi",
    hu: "hu",
    hy: "hy",
    id: "id",
    is: "is",
    it: "it",
    ja: "ja",
    ka: "ka",
    kk: "kk",
    km: "km",
    kn: "kn",
    ko: "ko",
    ky: "ky",
    lt: "lt",
    lv: "lv",
    mn: "mn",
    mr: "mr",
    ms: "ms",
    my: "my",
    nb: "no",
    nl: "nl",
    pa: "pa",
    pl: "pl",
    pt: "pt-br",
    "pt-pt": "pt",
    ro: "ro",
    ru: "ru",
    sk: "sk",
    sl: "sl",
    sq: "sq",
    sr: "sr",
    sv: "sv",
    sw: "sw",
    ta: "ta",
    te: "te",
    th: "th",
    tr: "tr",
    uk: "ua",
    ur: "ur",
    uz: "uz",
    vi: "vi",
    "zh-hans": "zh-cn",
    "zh-hant": "zh-hk",
    zu: "zu",
} as const;

/**
 * Renders a localized version of Google’s “Get it on Google Play”
 * badge. Uses the kaLocale of the request to choose an image. If the
 * locale is not recognized or not provided by Google the badge will
 * fall back to U.S. English.
 *
 * Notes on maintaining badge images
 *
 * Why do we host our own copies of Play badge images? Unfortunately,
 * Google builds margin _into_ their badge images. This makes it
 * challenging to consistently size, align and space Play badges with
 * the badges of other app stores.
 *
 * Worse yet, the margins included in the images vary from locale to
 * locale. For example, the English badge has margin on 4 sides while
 * the German badge has margin on only 2 sides (top and bottom).
 *
 * To make the appearance and behavior of the badges predictable we
 * host our own copies with the margins trimmed out.
 *
 * When adding a new badge be sure to run the image through Imagemagick
 * like so:
 *
 *     magick google-play-badge.png -trim ~/khan/webapp/images/store-badges/google-play/<Play locale code>.png
 *
 * Note that one unfortunate tradeoff of this approach is that if the
 * design of the Google Play badge has changed we must re-download and
 * re-process the badge images of all locales. (Even so, at the time
 * of writing the Play badge design provided by Google is not consistent
 * across all locales. Some locales use outdated coloring.)
 *
 * @see {@link https://play.google.com/intl/en_us/badges/|Badge Generator}
 *      to download new badge images.
 */
export default function GooglePlayBadge(
    props: GooglePlayBadgeProps,
): React.ReactElement {
    const targetKaLocale = RequestInfo.kaLocale;

    const playLocale =
        KA_LOCALE_TO_PLAY_LOCALE[targetKaLocale] || KA_LOCALE_TO_PLAY_LOCALE.en;

    const badgeImageUrl = `/images/store-badges/google-play/${playLocale}.png`;

    return (
        <StoreBadge
            safeAppDownloadUrl={props.safeAppDownloadUrl}
            badgeImageUrl={badgeImageUrl}
            altText={t`Get it on Google Play`}
            light={props.light}
            style={props.style}
        />
    );
}
